import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, ApiError, type User } from "../api/client";
import { bindCacheOwner, clearCache } from "./swr";

// Module-level cache so AppShell (which doesn't run useAuthGate) can render
// nav filtered by subscription without a duplicate /me request. useAuthGate
// publishes to this; useCurrentUser subscribes. Null while loading.
//
// The last known user is also persisted to localStorage so a page refresh
// paints the app shell instantly instead of blanking until /me resolves —
// /me still runs and corrects the state (incl. the 401 → /login redirect).
const ME_KEY = "logbuch:me:v1";
let _cachedUser: User | null = readPersistedUser();
const _userListeners = new Set<(u: User | null) => void>();

function readPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function publishUser(u: User | null) {
  _cachedUser = u;
  try {
    if (u === null) localStorage.removeItem(ME_KEY);
    else localStorage.setItem(ME_KEY, JSON.stringify(u));
  } catch {}
  // Data caches are household-scoped: binding wipes them when a different
  // user or household shows up on this device.
  if (u) bindCacheOwner(`${u.id}:${u.household_id ?? "none"}`);
  _userListeners.forEach((fn) => fn(u));
}

/** Drop every cached identity + data blob (logout, household switch). */
export function clearUserCaches() {
  _cachedUser = null;
  try { localStorage.removeItem(ME_KEY); } catch {}
  clearCache();
}

/** Background re-fetch of /me (soft refresh path). Errors are ignored —
 *  the regular useAuthGate flow owns redirects. */
export async function revalidateUser(): Promise<void> {
  try {
    publishUser(await auth.me());
  } catch {}
}

/** Push an updated user into the shared cache (e.g. nach accept-privacy),
 *  damit alle useCurrentUser-Abonnenten sofort den neuen Stand sehen. */
export function updateCachedUser(u: User) {
  publishUser(u);
}

/**
 * Drop-in auth gate for routed pages.
 *
 * Returns `{ user, loading }`. While loading the page should render nothing.
 * On 401 → redirects to /login. If the authenticated user `needs_setup` and
 * is not already on a setup-related route, redirects to /setup.
 */
export function useAuthGate() {
  const nav = useNavigate();
  const loc = useLocation();
  // Cache-first: the last known user renders the page immediately; /me
  // revalidates in the background and corrects (or redirects) afterwards.
  const [user, setUser] = useState<User | null>(_cachedUser);
  const [loading, setLoading] = useState(_cachedUser === null);

  useEffect(() => {
    // OIDC bounceback: backend redirected us with the Keycloak access+refresh
    // tokens in the URL fragment. Stash them, then clear the hash so they
    // never linger in window.location / history.
    if (auth.consumeCallbackHash()) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }

    // A cached user that still needs setup shouldn't see a flash of the
    // regular app before /me confirms it.
    if (_cachedUser?.needs_setup && !loc.pathname.startsWith("/setup")) {
      nav("/setup", { replace: true });
    }

    auth
      .me()
      .then((u) => {
        setUser(u);
        publishUser(u);
        setLoading(false);
        const onSetup = loc.pathname.startsWith("/setup");
        if (u.needs_setup && !onSetup) {
          nav("/setup", { replace: true });
        }
        // No global subscription gate: the whole app is free by default.
        // Subscription only hides three specific features (see hasPaid()
        // below + per-feature conditionals in /zuordnung nav, "Regeln
        // anwenden" buttons, and bank-connection auto-sync frequency).
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          clearUserCaches();
          nav("/login", { replace: true });
        } else if (err instanceof ApiError && err.status === 402) {
          // 402 = subscription_required. /me itself shouldn't 402 (it's not
          // gated), but a domain endpoint hitting useAuthGate indirectly might.
          nav("/einstellungen", { replace: true });
        } else {
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { user, loading };
}

/**
 * Lightweight subscribe-to-cached-user hook for components that need the
 * user but don't want useAuthGate's redirect side-effects (e.g. AppShell
 * has to render before knowing the user, then refresh once /me resolves).
 */
export function useCurrentUser(): User | null {
  const [u, setU] = useState<User | null>(_cachedUser);
  useEffect(() => {
    _userListeners.add(setU);
    return () => {
      _userListeners.delete(setU);
    };
  }, []);
  return u;
}

/**
 * Does this user currently have access to Logbuch Plus features?
 *
 * Plus-only features are: /zuordnung (Regeln + Auto-Scan), "Regeln anwenden"
 * buttons on /budget + /umsätze, and the automatic background bank import
 * (the scheduler — manual "Bank laden" stays free for everyone).
 *
 * Returns false while user is still loading so we don't briefly flash paid
 * UI then hide it. When billing is disabled (dev / self-host) every user
 * counts as paid.
 */
export function hasPaid(user: User | null): boolean {
  const sub = user?.subscription;
  if (!sub) return false;
  if (sub.billing_disabled) return true;
  return !!sub.has_access;
}

export function fmtEUR(amount: string | number): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);
}

const ANON_KEY = "finance-app:anonym";

export function useAnonymMode(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(ANON_KEY) === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ANON_KEY, on ? "1" : "0");
    }
  }, [on]);
  return [on, setOn];
}
